import * as Sentry from '@sentry/browser';
import summaryToMarkdown from '@sr/rich-text/summary-to-markdown/src';
import { getDefaultCustomizations } from '@sr/templates/src';
import {
  About,
  BlockListSection,
  BlockListSectionItem,
  ContentType,
  DetailSection,
  DetailSectionItem,
  InlineListSection,
  JobProfession,
  Resume,
  ResumeVisibility,
  ReviewRequestStatus,
  Section,
  SectionType,
  Slug,
  TemplateId,
  User,
} from '@sr/types';
import Cookies from 'js-cookie';
import { nanoid } from 'nanoid';
import trackEvent, { trackMixpanelUntypedEvent } from '../../utils/analytics';
import { auth, ref, firebase } from './firebase';
import { sampleResume } from './sample-resume';
import isBot from 'utils/is-bot';

const API = {
  slug: {
    isAvailable({ slug }: { slug: string }): Promise<boolean> {
      return new Promise((resolve, reject) => {
        ref
          .child('slugs2')
          .child(slug)
          .once('value', function (slugSnapshot) {
            const slug = slugSnapshot.val();
            resolve(!slug);
          });
      });
    },
  },

  user: {
    changeName({
      userId,
      firstName,
      lastName,
    }: {
      userId: string;
      firstName: string;
      lastName: string;
    }): Promise<any> {
      const updates = {
        [`users2/${userId}/firstName`]: firstName,
        [`users2/${userId}/lastName`]: lastName,
      };
      return ref.update(updates);
    },
    changePassword({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }): Promise<any> {
      const user = firebase.auth().currentUser;

      // This shouldnt ever happen
      if (!user || !user.email) {
        Sentry.captureException(
          new Error('Tried to change password without being authed')
        );
        return Promise.reject();
      }

      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      return user.reauthenticateWithCredential(credential).then(() => {
        return user.updatePassword(newPassword);
      });
    },
    async delete({ password }: { password: string }) {
      const user = firebase.auth().currentUser;

      // This shouldnt ever happen
      if (!user || !user.email) {
        console.error('Tried to delete user without being authed');
        throw new Error('Invalid password');
      }

      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        password
      );

      // Reauth the user. Required for delete
      await user.reauthenticateWithCredential(credential);

      try {
        await API.events.logEvent({
          userId: user.uid,
          eventName: 'Account Deleted',
        });
      } catch (e) {}

      // Delete the user from Firebase authentication
      // A cloud function is triggered by this that removes the user and
      // their resumes from the database.
      await user.delete();
      const SURVEY_URL = `https://standardresume.typeform.com/to/hyeQb7FE#id=${
        user.uid
      }&email=${encodeURIComponent(user.email || '')}`;
      window.open(SURVEY_URL);
    },
  },

  resume: {
    get(resumeId: string): Promise<Resume> {
      return new Promise((resolve, reject) => {
        ref.child(`resumes2/${resumeId}`).once('value', function (snapshot) {
          const resume: Resume = snapshot.val();
          resolve(resume);
        });
      });
    },
    create({
      userId,
      resume,
    }: {
      userId: string;
      resume: Resume;
    }): Promise<Resume> {
      // Collect the updates required to create a resume
      const updates: Dict = {};
      updates[`users2/${userId}/resumes/${resume.id}`] = resume.name;

      // Add to the slug tree for the new resme
      updates[`slugs2/${resume.slug}`] = {
        id: resume.slug,
        ownerId: userId,
        resumeId: resume.id,
        visibility: resume.visibility,
      };

      // Add the resume to the resumes2 tree
      updates[`resumes2/${resume.id}`] = resume;

      return new Promise((resolve, reject) => {
        ref
          .update(updates)
          .then(() => resolve(resume))
          .catch((err) => reject(err));
      });
    },
    async duplicate({
      resumeId,
      resumeName,
    }: {
      resumeId: string;
      resumeName?: string;
    }): Promise<string> {
      const resume = await API.resume.get(resumeId);
      const newResumeId = nanoid();
      resume.name = resumeName || `${resume.name} Copy`;
      resume.id = newResumeId;
      resume.slug = nanoid();
      resume.isDefault = false;

      // Update the resumeId of each section in the resume
      if (resume.sections) {
        Object.keys(resume.sections || {}).forEach((sectionId) => {
          resume.sections[sectionId].resumeId = newResumeId;
        });
      }

      await API.resume.create({ userId: resume.owner, resume });
      return resume.id;
    },
    async delete({ resumeId, user }: { resumeId: string; user: User }) {
      const numResumes = Object.keys(user.resumes || {}).length;
      if (numResumes === 1) {
        alert(
          "You can't delete your only resume. Please create another resume and then delete this one."
        );
      } else {
        return API.resume.get(resumeId).then((resume) => {
          const updates: Dict = {};
          updates[`resumes2/${resume.id}`] = null;
          updates[`slugs2/${resume.slug}`] = null;
          updates[`users2/${resume.owner}/resumes/${resume.id}`] = null;
          return ref.update(updates);
        });
      }
    },
    updateSectionsOrder({
      resumeId,
      sectionsOrder,
    }: {
      resumeId: string;
      sectionsOrder: string[];
    }) {
      const updates: Dict = {};
      updates[`resumes2/${resumeId}/sectionsOrder`] = sectionsOrder;
      return ref.update(updates);
    },
    updateName({ resumeId, name }: { resumeId: string; name: string }) {
      return API.resume.get(resumeId).then((resume) => {
        const updates: Dict = {};
        updates[`resumes2/${resume.id}/name`] = name;
        updates[`users2/${resume.owner}/resumes/${resume.id}`] = name;
        return ref.update(updates);
      });
    },
    updateVisibility({
      resume,
      visibility,
    }: {
      resume: Resume;
      visibility: ResumeVisibility;
    }): Promise<void> {
      const updates: Dict = {};
      updates[`resumes2/${resume.id}/visibility`] = visibility;
      updates[`slugs2/${resume.slug}/visibility`] = visibility;
      return ref.update(updates);
    },
    updateSlug({
      resume,
      slug,
    }: {
      resume: Resume;
      slug: string;
    }): Promise<void> {
      const updates: Dict = {};
      // Update the resumes slug property
      updates[`resumes2/${resume.id}/slug`] = slug;
      // Add the new slug
      updates[`slugs2/${slug}`] = {
        id: slug,
        ownerId: resume.owner,
        resumeId: resume.id,
        visibility: resume.visibility,
      };
      // Clear the old slug
      updates[`slugs2/${resume.slug}`] = null;
      return ref.update(updates);
    },
    updateProfilePhotoUrl({
      resume,
      profilePhotoUrl,
      profilePhotoSrc,
    }: {
      resume: Resume;
      profilePhotoUrl: string | null;
      profilePhotoSrc: string | null;
    }): Promise<void> {
      const updates: Dict = {};
      updates[`resumes2/${resume.id}/profilePhotoUrl`] = profilePhotoUrl;
      updates[`resumes2/${resume.id}/profilePhotoSrc`] = profilePhotoSrc;
      return ref.update(updates);
    },

    // Used to set individual properties from write forms
    setProperty: ({ path, value }: { path: string; value: any }) => {
      ref.child(`resumes2/${path}`).set(value);
    },

    updateProperties: ({
      resumeId,
      updates,
    }: {
      resumeId: string;
      updates: {
        [K in string]: any;
      };
    }) => {
      // Append all keys with resumes2/{resumeId}/
      const modifiedUpdates: Dict = {};
      for (const [key, value] of Object.entries(updates)) {
        modifiedUpdates[`resumes2/${resumeId}/${key}`] = value;
      }
      return ref.update(modifiedUpdates);
    },

    isUsingMarkdown(resume: Resume): boolean {
      const sections = Object.values(resume.sections || {});
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        if (section.type === 'detail') {
          const items = Object.values(section.items || {});
          for (let j = 0; j < items.length; j++) {
            const item = items[j];
            // If an item isn't using markdown, consider the whole resume not upgraded
            if (item.textType !== 'markdown') {
              return false;
            }
          }
        }
      }
      return true;
    },

    async upgradeToMarkdown(resume: Resume): Promise<void> {
      // Start by creating a duplicate of the resume for backup
      await API.resume.duplicate({
        resumeId: resume.id,
        resumeName: `${resume.name} (Backup)`,
      });

      // Store all of the fields to update as part of the upgrade
      const updates: { [path: string]: string } = {};

      // Look through all sections
      Object.values(resume.sections || {}).forEach((section) => {
        if (section.type === 'detail') {
          // Look through all items of a detail section
          Object.values(section.items || {}).forEach((item) => {
            // Convert the legacy summary string to markdown and add updates
            const markdown = summaryToMarkdown(item.summary);
            updates[
              `/sections/${section.sectionId}/items/${item.id}/textType`
            ] = 'markdown';
            updates[`/sections/${section.sectionId}/items/${item.id}/summary`] =
              markdown;
          });
        }
      });

      // Write all updates to the resume
      await API.resume.updateProperties({
        resumeId: resume.id,
        updates,
      });
    },

    getMetrics(resumeId: string): Promise<ResumeMetrics> {
      return new Promise((resolve, reject) => {
        ref
          .child(`tracking2/${resumeId}/views`)
          .once('value', function (viewsSnapshot) {
            const views: TrackingView[] = Object.values(
              viewsSnapshot.val() || {}
            );
            resolve({
              viewsToday: getViewsToday(views),
              viewsThisWeek: getViewsThisWeek(views),
            });
          })
          .catch((error) => reject(error));
      });
    },
  },

  section: {
    get({
      sectionId,
      resumeId,
    }: {
      sectionId: string;
      resumeId: string;
    }): Promise<Section> {
      return new Promise((resolve, reject) => {
        ref
          .child(`resumes2/${resumeId}/sections/${sectionId}`)
          .once('value', function (snapshot) {
            const section: Section = snapshot.val();
            resolve(section);
          });
      });
    },
    create({
      resume,
      type,
      contentType,
      title,
      list,
      addItem = false,
    }: {
      resume: Resume;
      type: SectionType;
      contentType: ContentType;
      title: string;
      list?: string;
      addItem?: boolean;
    }): Promise<string> {
      return new Promise((resolve, reject) => {
        const resumeRef = ref.child(`resumes2/${resume.id}`);
        const sectionId = resumeRef.child('sections').push().key;
        if (sectionId) {
          let newSection = emptySection({
            resumeId: resume.id,
            sectionId,
            type,
            contentType,
            title,
            addItem,
          });
          if (type === 'inline-list' && list) {
            // Cast to inline list section and add list property
            newSection = newSection as InlineListSection;
            newSection.list = list;
          }
          const updates = {
            sectionsOrder: (resume.sectionsOrder || []).concat([sectionId]),
            [`sections/${sectionId}`]: newSection,
          };
          resumeRef
            .update(updates)
            .then(() => {
              // Get the newly created section
              return API.section.get({ sectionId, resumeId: resume.id });
            })
            .then((section) => {
              // Add an item to the newly created section if it isn't an inline list (they dont have items)
              if (section.type !== 'inline-list') {
                return API.sectionItem.create({ section });
              }
            })
            .then(() => resolve(sectionId))
            .catch((error) => {
              Sentry.captureException(error);
              reject(error);
            });
        } else {
          Sentry.captureException(
            new Error(
              'Failed to generate a key to create section using .push().key'
            )
          );
          reject();
        }
      });
    },

    delete({ resume, section }: { resume: Resume; section: Section }) {
      const resumeRef = ref.child(`resumes2/${section.resumeId}`);
      const updates = {
        sectionsOrder: (resume.sectionsOrder || []).filter(
          (id) => id !== section.sectionId
        ),
        [`sections/${section.sectionId}`]: null,
      };
      resumeRef.update(updates);
    },

    update({
      section,
      updates,
    }: {
      section: Section;
      updates: {
        [K in string]: any;
      };
    }) {
      const sectionRef = ref.child(
        `resumes2/${section.resumeId}/sections/${section.sectionId}`
      );
      sectionRef.update(updates);
    },
  },

  sectionItem: {
    create({ section }: { section: Section }) {
      const sectionRef = ref.child(
        `resumes2/${section.resumeId}/sections/${section.sectionId}`
      );
      const itemId = sectionRef.child('items').push().key;
      if (itemId) {
        const updates: Dict = {};
        if (section.type !== 'inline-list') {
          updates['itemsOrder'] = (section.itemsOrder || []).concat([itemId]);
        }
        if (section.type === 'detail') {
          updates[`items/${itemId}`] = emptyDetailSectionItem(itemId);
        } else if (section.type === 'block-list') {
          updates[`items/${itemId}`] = emptyBlockListSectionItem(itemId);
        } else if (section.type === 'inline-list') {
          // Do nothing. An inline list has no SectionItems.
        }
        return sectionRef.update(updates).then(() => {
          return itemId;
        });
      } else {
        Sentry.captureException(
          new Error(
            'Failed to generate a key to create section using .push().key'
          )
        );
        return Promise.reject();
      }
    },

    delete({
      section,
      sectionItemId,
    }: {
      section: DetailSection | BlockListSection;
      sectionItemId: string;
    }) {
      const sectionRef = ref.child(
        `resumes2/${section.resumeId}/sections/${section.sectionId}`
      );
      const updates = {
        itemsOrder: (section.itemsOrder || []).filter(
          (id) => id !== sectionItemId
        ),
        [`items/${sectionItemId}`]: null,
      };
      sectionRef.update(updates);
    },
  },
  events: {
    logEvent({
      userId,
      eventName,
      properties,
    }: {
      userId: string;
      eventName: string;
      properties?: {
        [K in string]: any;
      };
    }): Promise<void> {
      // This is a hack to avoid updating v1 code that will be deleted
      // Sends the event directly to Mixpanel instead of logging in our
      // deprecated events database.
      trackMixpanelUntypedEvent(eventName, properties);
      return Promise.resolve();
    },
  },
  auth: {
    signIn({ email, password }: { email: string; password: string }) {
      return auth
        .signInWithEmailAndPassword(email, password)
        .then((userCredential) => {
          if (
            userCredential &&
            userCredential.user &&
            typeof userCredential.user.uid === 'string'
          ) {
            API.events.logEvent({
              userId: userCredential.user.uid,
              eventName: 'Signed In',
              properties: {
                'Completed Sign In Page': 'True',
              },
            });
          } else {
            Sentry.captureException(
              new Error('Sign in UserCredential missing user id.')
            );
          }
        });
    },
    signOut({ userId }: { userId: string }) {
      trackEvent.signedOut();
      // Give Mixpanel a small window to send the Signed Out event
      setTimeout(async () => {
        // Unauthenticate the user and reload the page to clear state
        await auth.signOut();
        window.location.reload();
      }, 500);
    },
  },
};

function emptyDetailSectionItem(id: string): DetailSectionItem {
  return {
    type: 'detail',
    id,
    title: '',
    subtitle: '',
    website: '',
    isCurrent: false,
    summary: '',
    textType: 'markdown',
  };
}

function emptyBlockListSectionItem(id: string): BlockListSectionItem {
  return {
    type: 'block-list',
    id,
    primary: '',
    secondary: '',
  };
}

function emptySection({
  resumeId,
  sectionId,
  type,
  contentType,
  title,
  addItem = false,
}: {
  resumeId: string;
  sectionId: string;
  type: SectionType;
  contentType: ContentType;
  title: string;
  addItem?: boolean;
}): Section {
  let section: Section = {
    resumeId,
    sectionId,
    type,
    contentType,
    title,
  };
  if (type === 'inline-list') {
    section = section as InlineListSection;
    section.list = '';
  }
  if (type === 'detail' && addItem) {
    section = section as DetailSection;
    const item = emptyDetailSectionItem(nanoid());
    section.items = { [item.id]: item };
    section.itemsOrder = [item.id];
  }
  return section;
}

// Consider today to be the last 24 hours
function getViewsToday(views: TrackingView[]) {
  const now = new Date().getTime();
  const todayStart = now - 1000 * 60 * 60 * 24;
  return views.filter((view) => view.timestamp > todayStart).length;
}

// Consider this week to be the last 7 days (168 hours)
function getViewsThisWeek(views: TrackingView[]) {
  const now = new Date().getTime();
  const weekStart = now - 1000 * 60 * 60 * 24 * 7;
  return views.filter((view) => view.timestamp > weekStart).length;
}

type Dict = {
  [key: string]: any;
};

export type ResumeMetrics = {
  viewsToday: number;
  viewsThisWeek: number;
};

type TrackingView = {
  timestamp: number;
};

export default API;
